/**
 * Releases API — Endpoints 1, 2, 4, 5, 6, 10, 12
 *
 * API 1  : GET    /api/releases              → List all releases
 * API 2  : POST   /api/releases              → Create a new release (DRAFT)
 * API 4  : PATCH  /api/releases/{id}/status   → Move DRAFT → READY
 * API 5  : POST   /api/releases/{id}/execute  → Execute release (go LIVE)
 * API 6  : GET    /api/releases/{id}/clients  → Who received this release?
 * API 10 : GET    /api/releases/{id}          → Get a single release by ID
 * API 12 : DELETE /api/releases/{id}          → Delete a DRAFT or READY release
 */
import { apiFetch, buildUrl } from "./apiClient";

/**
 * API 1 — List all releases (paginated).
 * @param {number} [limit=20]
 * @param {string} [cursor]
 * @returns {Promise<{ data: Release[], meta: { cursor?: string } }>}
 */
export async function fetchReleases(limit = 20, cursor) {
    const path = buildUrl("/api/releases", { limit, cursor });
    return apiFetch(path);
}

/**
 * API 2 — Create a new release (always starts as DRAFT).
 * @param {{ releaseName: string, createdBy: string }} payload
 * @returns {Promise<{ data: Release, meta: object }>}
 */
export async function createRelease({ releaseName, createdBy }) {
    const name = (releaseName || "").trim();
    const by = (createdBy || "").trim();
    if (!name) throw new Error("Release name is required.");
    if (!by) throw new Error("Created by is required.");

    return apiFetch("/api/releases", {
        method: "POST",
        body: JSON.stringify({ releaseName: name, createdBy: by }),
    });
}

/**
 * API 4 — Transition release status.
 * Only allowed transition from frontend: DRAFT → READY.
 * (RELEASED is set automatically by executeRelease)
 * @param {string} releaseId
 * @param {"READY"} status
 * @returns {Promise<{ data: Release, meta: object }>}
 */
export async function updateReleaseStatus(releaseId, status) {
    if (!releaseId) throw new Error("releaseId is required.");
    if (!status) throw new Error("status is required.");

    return apiFetch(`/api/releases/${encodeURIComponent(releaseId)}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
    });
}

/**
 * API 5 — Execute a READY release (go LIVE).
 * - GENERIC features → all live clients automatically.
 * - CLIENT features  → only clients in clientMap.
 *
 * @param {string} releaseId - must be READY
 * @param {{ [featureId: string]: string[] }} [clientMap] - only for CLIENT-type features
 * @returns {Promise<{ data: { release: Release, summary: { featuresProcessed: number, clientReleaseItemsCreated: number } }, meta: object }>}
 */
export async function executeRelease(releaseId, clientMap) {
    if (!releaseId) throw new Error("releaseId is required.");

    const hasClientMap = clientMap && Object.keys(clientMap).length > 0;

    return apiFetch(`/api/releases/${encodeURIComponent(releaseId)}/execute`, {
        method: "POST",
        // For GENERIC-only releases, send NO body at all — some API Gateway
        // integrations return 500 when they receive an unexpected body.
        ...(hasClientMap
            ? { body: JSON.stringify({ clientMap }) }
            : {}
        ),
    });
}

/**
 * API 6 — Get all clients that received a specific release.
 * @param {string} releaseId
 * @returns {Promise<{ data: { clientIds: string[], count: number }, meta: object }>}
 */
export async function fetchReleaseClients(releaseId) {
    if (!releaseId) throw new Error("releaseId is required.");
    return apiFetch(`/api/releases/${encodeURIComponent(releaseId)}/clients`);
}

/**
 * API 10 — Get a single release by ID.
 * Call this when navigating to the Release Detail page for fresh data.
 *
 * @param {string} releaseId
 * @returns {Promise<{ data: Release, meta: object }>}
 */
export async function fetchRelease(releaseId) {
    if (!releaseId) throw new Error("releaseId is required.");
    return apiFetch(`/api/releases/${encodeURIComponent(releaseId)}`);
}

/**
 * API 12 — Delete a release (DRAFT or READY only).
 * RELEASED releases cannot be deleted — the API returns 409.
 *
 * @param {string} releaseId
 * @returns {Promise<{ data: { releaseId: string, deleted: boolean }, meta: object }>}
 */
export async function deleteRelease(releaseId) {
    if (!releaseId) throw new Error("releaseId is required.");
    return apiFetch(`/api/releases/${encodeURIComponent(releaseId)}`, {
        method: "DELETE",
    });
}
